import { useRef, useEffect, useState, useCallback, useMemo } from 'preact/hooks'
import type { Message, Persona, ChatAttachment } from '../types'
import { PERSONA_INFO } from '../types'
import { MessageBubble, precedingUserRequest } from './MessageBubble'
import { ChatInput } from './ChatInput'
import {
  fetchDebugMessageHistory,
  getQuotaStatus,
  getPersonaDescription,
  updatePersonaMemoryField,
  listCloudDiaryEntries,
  upsertCloudDiaryEntry,
  deleteCloudDiaryEntry,
} from '../services/api'
import type { CloudPersona, CloudDiaryEntry } from '../services/api'
import { FullscreenImageViewer } from './FullscreenImageViewer'
import { playTts, stopTts } from '../services/ttsApi'
import type { TtsSettings } from '../services/ttsApi'
import { setOnStateChange, stopAudioQueue } from '../services/audioQueue'

type MemorySectionKey = 'persona' | 'user_details' | 'diary' | 'future_events'

interface ChatViewProps {
  messages: Message[]
  isLoading: boolean
  isSessionActive: boolean
  voiceChatId: string | null
  onSend: (text: string, attachments?: ChatAttachment[]) => void
  onStartSession: () => void
  /** Replace a user message with new content and resend, dropping any
   *  newer messages. Wired to useChat.editMessageAndResend. */
  onEditMessage?: (messageId: string, newContent: string) => void
  /** Drop a message and all newer ones from the displayed history. */
  onDeleteMessage?: (messageId: string) => void
  personaName: string
  personaImageUrl?: string
  ttsSettings?: TtsSettings
  onNavigate?: (view: string) => void
  cloudPersona?: CloudPersona
  sidebarVisible?: boolean
  onToggleSidebar?: () => void
  personas?: Persona[]
  selectedPersona?: Persona
  onSelectPersona?: (persona: Persona) => void
  userEmail?: string
  /** Refresh persona data after the user edits a memory field. The
   *  parent owns cloudPersonas state, so this re-fetches the cloud
   *  copy and updates the panel/modal in place. */
  onPersonaUpdated?: () => void
  /** Start a voice call with the persona (components/VoiceCallScreen.tsx). */
  onStartCall?: () => void
}

export function ChatView({
  messages,
  isLoading,
  isSessionActive,
  voiceChatId,
  onSend,
  onStartSession,
  onEditMessage,
  onDeleteMessage,
  personaName,
  personaImageUrl,
  ttsSettings,
  onNavigate,
  cloudPersona,
  sidebarVisible,
  onToggleSidebar,
  personas,
  selectedPersona,
  onSelectPersona,
  userEmail,
  onPersonaUpdated,
  onStartCall,
}: ChatViewProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [showContext, setShowContext] = useState(false)
  const [contextData, setContextData] = useState<Record<string, unknown> | null>(null)
  const [contextLoading, setContextLoading] = useState(false)
  const [contextError, setContextError] = useState<string | null>(null)
  const [showMemory, setShowMemory] = useState<MemorySectionKey | null>(null)
  const [rightPanelVisible, setRightPanelVisible] = useState(true)
  const [ttsEnabled, setTtsEnabled] = useState(false)
  // Edit-message modal state (matches the AlertDialog flow in
  // text_chat_screen.dart's `_showEditMessageDialog`).
  const [editTarget, setEditTarget] = useState<{ id: string; original: string } | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const editTrailingCount = useMemo(() => {
    if (!editTarget) return 0
    const idx = messages.findIndex((m) => m.id === editTarget.id)
    return idx < 0 ? 0 : messages.length - idx - 1
  }, [editTarget, messages])
  const [audioPlaying, setAudioPlaying] = useState(false)
  // Persona avatar fullscreen overlay — set to the image URL on click,
  // cleared on close. Same UX as note attachments / generated images.
  const [personaImageFullscreen, setPersonaImageFullscreen] = useState<string | null>(null)
  const lastStreamingId = useRef<string | null>(null)

  const personaRole = PERSONA_INFO[selectedPersona?.id ?? '']?.role ?? ''

  // Wire audio queue state changes to React
  useEffect(() => {
    setOnStateChange(setAudioPlaying)
    return () => {
      setOnStateChange(null)
      stopAudioQueue()
    }
  }, [])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages])

  // Auto-play TTS when an assistant message finishes streaming
  useEffect(() => {
    if (!ttsEnabled || !voiceChatId || messages.length === 0) return

    // Track which message is currently streaming
    const streamingMsg = messages.find((m) => m.role === 'assistant' && m.isStreaming)
    if (streamingMsg) {
      lastStreamingId.current = streamingMsg.id
      return
    }

    // A message we were tracking just finished streaming
    if (lastStreamingId.current) {
      const finished = messages.find((m) => m.id === lastStreamingId.current)
      lastStreamingId.current = null
      if (finished?.content?.trim()) {
        playTts(finished.content, voiceChatId, ttsSettings)
      }
    }
  }, [messages, ttsEnabled, voiceChatId, ttsSettings])

  const handlePlayMessage = useCallback((text: string) => {
    const chatId = voiceChatId || `tts_${Date.now()}`
    playTts(text, chatId, ttsSettings)
  }, [voiceChatId, ttsSettings])

  const handleShowContext = async () => {
    if (!voiceChatId) return
    setShowContext(true)
    setContextLoading(true)
    setContextError(null)
    try {
      const data = await fetchDebugMessageHistory(voiceChatId)
      setContextData(data)
    } catch (e) {
      setContextError(String(e))
    } finally {
      setContextLoading(false)
    }
  }

  // Time-based greeting
  const getGreeting = () => {
    const h = new Date().getHours()
    if (h < 6) return 'Good night!'
    if (h < 12) return 'Good morning!'
    if (h < 17) return 'Good afternoon!'
    if (h < 22) return 'Good evening!'
    return 'Good night!'
  }

  const formatCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  }

  const formatCurrentTime = () => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (!isSessionActive) {
    return (
      <div class="chat-view" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div class="chat-view__header">
          {!sidebarVisible && onNavigate && (
            <button class="btn btn-sm" onClick={() => onNavigate('landing')} title="Home" style={{ fontSize: 18, padding: '4px 8px' }}>{'\u{1F3E0}'}</button>
          )}
          {!sidebarVisible && onToggleSidebar && (
            <button class="btn btn-sm" onClick={onToggleSidebar} title="Show sidebar" style={{ marginRight: 8, fontSize: 14, padding: '4px 8px' }}>
              {'\u{25B6}'}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {onStartCall && (
            <button class="btn btn-sm" onClick={onStartCall} title={`Call ${personaName}`} style={{ fontSize: 16, padding: '4px 10px' }}>
              {'\u{1F4DE}'}
            </button>
          )}
          <PersonaSelector personas={personas} selectedPersona={selectedPersona} onSelectPersona={onSelectPersona} personaName={personaName} />
          <button
            class="btn btn-sm"
            onClick={() => setRightPanelVisible((v) => !v)}
            title={rightPanelVisible ? 'Hide persona panel' : 'Show persona panel'}
            style={{ fontSize: 14, padding: '4px 8px' }}
          >
            {rightPanelVisible ? '\u{25B6}' : '\u{25C0}'}
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
        {/* Center: greeting */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h1 style={{ fontSize: 36, fontWeight: 300, color: 'var(--text)', marginBottom: 12 }}>{getGreeting()}</h1>
          <div style={{ color: 'var(--primary)', fontSize: 16, marginBottom: 16 }}>You're chatting with {personaName}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 4 }}>Today is {formatCurrentDate()}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 32 }}>{formatCurrentTime()}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button class="btn btn-primary" onClick={onStartSession} style={{ padding: '10px 24px', fontSize: 15 }}>
              New Chat
            </button>
            {onStartCall && (
              <button class="btn" onClick={onStartCall} style={{ padding: '10px 24px', fontSize: 15 }}>
                {'\u{1F4DE}'} Call {personaName}
              </button>
            )}
          </div>
        </div>
        {/* Right: persona hero + memory list */}
        {rightPanelVisible && (
          <PersonaPanel
            personaName={personaName}
            personaImageUrl={personaImageUrl}
            personaRole={personaRole}
            cloudPersona={cloudPersona}
            onOpenMemory={setShowMemory}
            onZoomImage={setPersonaImageFullscreen}
          />
        )}
        </div>{/* close row div */}
        {showMemory && cloudPersona && (
          <MemoryModal
            personaName={personaName}
            personaImageUrl={personaImageUrl}
            personaId={selectedPersona?.id ?? ''}
            userEmail={userEmail}
            cloudPersona={cloudPersona}
            initialSection={showMemory}
            onClose={() => setShowMemory(null)}
            onPersonaUpdated={onPersonaUpdated}
          />
        )}
        {personaImageFullscreen && (
          <FullscreenImageViewer
            src={personaImageFullscreen}
            alt={personaName}
            onClose={() => setPersonaImageFullscreen(null)}
          />
        )}
      </div>
    )
  }

  const hasMessages = messages.length > 0

  return (
    <div class="chat-view" style={{ display: 'flex', flexDirection: 'row' }}>
      {/* Main chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div class="chat-view__header">
          {!sidebarVisible && onNavigate && (
            <button class="btn btn-sm" onClick={() => onNavigate('landing')} title="Home" style={{ fontSize: 18, padding: '4px 8px' }}>{'\u{1F3E0}'}</button>
          )}
          {!sidebarVisible && onToggleSidebar && (
            <button class="btn btn-sm" onClick={onToggleSidebar} title="Show sidebar" style={{ marginRight: 8, fontSize: 14, padding: '4px 8px' }}>
              {'\u{25B6}'}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            class="btn btn-sm"
            onClick={handleShowContext}
            disabled={contextLoading}
            title="Backend message history (debug)"
            style={{ fontSize: 12, padding: '4px 8px' }}
          >
            {contextLoading ? '...' : 'Debug'}
          </button>
          {audioPlaying && (
            <button class="tts-stop" onClick={() => stopTts()}>
              Stop Audio
            </button>
          )}
          <button
            class={`tts-toggle ${ttsEnabled ? 'tts-toggle--active' : ''}`}
            onClick={() => {
              if (ttsEnabled) stopAudioQueue()
              setTtsEnabled((p) => !p)
            }}
            title={ttsEnabled ? 'Auto-play TTS on' : 'Auto-play TTS off'}
          >
            {ttsEnabled ? '\u{1F50A}' : '\u{1F507}'}
          </button>
          {onStartCall && (
            <button class="btn btn-sm" onClick={onStartCall} title={`Call ${personaName}`} style={{ fontSize: 16, padding: '4px 10px' }}>
              {'\u{1F4DE}'}
            </button>
          )}
          <PersonaSelector personas={personas} selectedPersona={selectedPersona} onSelectPersona={onSelectPersona} personaName={personaName} />
          <button
            class="btn btn-sm"
            onClick={() => setRightPanelVisible((v) => !v)}
            title={rightPanelVisible ? 'Hide persona panel' : 'Show persona panel'}
            style={{ fontSize: 14, padding: '4px 8px' }}
          >
            {rightPanelVisible ? '\u{25B6}' : '\u{25C0}'}
          </button>
        </div>
        <div class="chat-view__messages" ref={messagesContainerRef}>
          {!hasMessages && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
              <h1 style={{ fontSize: 32, fontWeight: 300, color: 'var(--text)', marginBottom: 8 }}>{getGreeting()}</h1>
              <div style={{ color: 'var(--primary)', fontSize: 15 }}>You're chatting with {personaName}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Today is {formatCurrentDate()} &middot; {formatCurrentTime()}</div>
            </div>
          )}
          {messages.map((msg, i) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              userRequest={msg.role === 'assistant' ? precedingUserRequest(messages, i) : undefined}
              onPlayTts={handlePlayMessage}
              isAudioPlaying={audioPlaying}
              onEditMessage={onEditMessage ? (id, current) => {
                setEditTarget({ id, original: current })
                setEditDraft(current)
              } : undefined}
              onDeleteMessage={onDeleteMessage ? (id) => {
                if (confirm('Delete this message? Any messages after it will also be removed.')) {
                  onDeleteMessage(id)
                }
              } : undefined}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput onSend={onSend} disabled={isLoading} personaId={selectedPersona?.id} />
      </div>
      {/* Right persona panel */}
      {rightPanelVisible && (
        <PersonaPanel
          personaName={personaName}
          personaImageUrl={personaImageUrl}
          personaRole={personaRole}
          cloudPersona={cloudPersona}
          onOpenMemory={setShowMemory}
          onZoomImage={setPersonaImageFullscreen}
        />
      )}

      {showContext && (
        <ContextModal
          data={contextData}
          loading={contextLoading}
          error={contextError}
          onClose={() => { setShowContext(false); setContextData(null); setContextError(null) }}
        />
      )}

      {showMemory && cloudPersona && (
        <MemoryModal
          personaName={personaName}
          personaImageUrl={personaImageUrl}
          personaId={selectedPersona?.id ?? ''}
          userEmail={userEmail}
          cloudPersona={cloudPersona}
          initialSection={showMemory}
          onClose={() => setShowMemory(null)}
          onPersonaUpdated={onPersonaUpdated}
        />
      )}
      {personaImageFullscreen && (
        <FullscreenImageViewer
          src={personaImageFullscreen}
          alt={personaName}
          onClose={() => setPersonaImageFullscreen(null)}
        />
      )}

      {editTarget && (
        <EditMessageDialog
          original={editTarget.original}
          draft={editDraft}
          onChange={setEditDraft}
          trailingCount={editTrailingCount}
          onCancel={() => { setEditTarget(null); setEditDraft('') }}
          onSubmit={() => {
            const trimmed = editDraft.trim()
            if (!trimmed || !onEditMessage) return
            onEditMessage(editTarget.id, trimmed)
            setEditTarget(null)
            setEditDraft('')
          }}
        />
      )}
    </div>
  )
}

// ── Persona Panel (right side — hero + memory list) ───────────

const MEMORY_ITEMS: { key: MemorySectionKey; label: string; icon: string }[] = [
  { key: 'persona',        label: 'Persona',        icon: '\u{2728}' },      // sparkles
  { key: 'user_details',   label: 'User Details',   icon: '\u{1F464}' },     // bust in silhouette
  { key: 'diary',          label: 'Diary',          icon: '\u{1F4D4}' },     // notebook
  { key: 'future_events',  label: 'Future Events',  icon: '\u{1F4C5}' },     // calendar
]

function PersonaSelector({
  personas,
  selectedPersona,
  onSelectPersona,
  personaName,
}: {
  personas?: Persona[]
  selectedPersona?: Persona
  onSelectPersona?: (persona: Persona) => void
  personaName: string
}) {
  if (!personas || personas.length <= 1 || !selectedPersona || !onSelectPersona) {
    return <span style={{ fontWeight: 500, marginRight: 8 }}>{personaName}</span>
  }
  return (
    <select
      style={{
        padding: '4px 6px', marginRight: 4,
        background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 6, color: 'var(--text)', fontSize: 13,
      }}
      value={selectedPersona.id}
      onChange={(e) => {
        const p = personas.find((p) => p.id === (e.target as HTMLSelectElement).value)
        if (p) onSelectPersona(p)
      }}
    >
      {personas.map((p) => (
        <option key={p.id} value={p.id}>{p.name}</option>
      ))}
    </select>
  )
}

function PersonaPanel({
  personaName,
  personaImageUrl,
  personaRole,
  cloudPersona,
  onOpenMemory,
  onZoomImage,
}: {
  personaName: string
  personaImageUrl?: string
  personaRole: string
  cloudPersona?: CloudPersona
  onOpenMemory: (section: MemorySectionKey) => void
  /** Called with the persona image URL when the user clicks the avatar.
   *  The parent owns the fullscreen overlay state. */
  onZoomImage: (url: string) => void
}) {
  return (
    <div style={{
      width: 220, borderLeft: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Body */}
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto' }}>
        {/* Persona hero */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          {personaImageUrl ? (
            <img
              src={personaImageUrl}
              alt={personaName}
              onClick={() => onZoomImage(personaImageUrl)}
              title="Click to view fullscreen"
              style={{ width: 120, height: 120, borderRadius: 20, objectFit: 'cover', cursor: 'zoom-in' }}
            />
          ) : (
            <div style={{
              width: 120, height: 120, borderRadius: 20,
              background: 'rgba(45,165,160,0.12)', border: '2px solid rgba(45,165,160,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56,
            }}>{'\u{1F916}'}</div>
          )}
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>{personaName}</div>
          {personaRole && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>{personaRole}</div>
          )}
        </div>

        {/* Memory list — visible to all users. The Persona modal itself
            gates the built-in personality section to owners; non-owners
            still see their own customization, user details, diary, etc. */}
        {cloudPersona && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 16 }}>
            {MEMORY_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => onOpenMemory(item.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', background: 'transparent',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  color: 'var(--text)', fontSize: 13, textAlign: 'left',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,165,160,0.1)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Edit Message Dialog ────────────────────────────────────────
//
// Mirrors `_showEditMessageDialog` in text_chat_screen.dart: edit the
// content, warn that any newer messages will be removed, then resend.

interface EditMessageDialogProps {
  original: string
  draft: string
  onChange: (text: string) => void
  trailingCount: number
  onCancel: () => void
  onSubmit: () => void
}

function EditMessageDialog({
  original,
  draft,
  onChange,
  trailingCount,
  onCancel,
  onSubmit,
}: EditMessageDialogProps) {
  const trimmed = draft.trim()
  const unchanged = trimmed === original.trim()
  return (
    <div class="context-modal__overlay" onClick={onCancel}>
      <div class="context-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div class="context-modal__header">
          <h3>Edit message</h3>
          <button class="btn btn-sm" onClick={onCancel}>✕</button>
        </div>
        <div class="context-modal__body" style={{ padding: 16 }}>
          <textarea
            value={draft}
            onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
            rows={6}
            autoFocus
            style={{
              width: '100%',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text)',
              padding: 10,
              fontSize: 14,
              lineHeight: 1.5,
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          {trailingCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--warning, #ff9800)', marginTop: 8 }}>
              This will remove {trailingCount} message{trailingCount === 1 ? '' : 's'} after this one.
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'flex-end' }}>
            <button class="btn btn-sm" onClick={onCancel}>Cancel</button>
            <button
              class="btn btn-sm btn-primary"
              onClick={onSubmit}
              disabled={!trimmed || unchanged}
            >Send</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Context Modal ──────────────────────────────────────────────

interface ContextModalProps {
  data: Record<string, unknown> | null
  loading: boolean
  error: string | null
  onClose: () => void
}

function ContextModal({ data, loading, error, onClose }: ContextModalProps) {
  return (
    <div class="context-modal__overlay" onClick={onClose}>
      <div class="context-modal" onClick={(e) => e.stopPropagation()}>
        <div class="context-modal__header">
          <h3>Backend Message History</h3>
          <button class="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        <div class="context-modal__body">
          {loading && <div class="context-modal__loading">Loading context...</div>}
          {error && <div class="context-modal__error">{error}</div>}
          {data && <ContextContent data={data} />}
        </div>
      </div>
    </div>
  )
}

function ContextContent({ data }: { data: Record<string, unknown> }) {
  const sessionInfo = data.session_info as Record<string, unknown> | undefined
  const messageAnalysis = data.message_analysis as Record<string, unknown> | undefined
  const fullHistory = data.full_message_history as Array<Record<string, unknown>> | undefined

  return (
    <div class="context-content">
      {/* Session Info */}
      {sessionInfo && (
        <ContextSection title="Session Info" color="var(--bg-card)">
          <div class="context-section__kv">
            {Object.entries(sessionInfo).map(([key, val]) => (
              <div key={key}>
                <span class="context-section__key">{key}:</span>{' '}
                <span>{typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}</span>
              </div>
            ))}
          </div>
        </ContextSection>
      )}

      {/* Message Analysis */}
      {messageAnalysis && (
        <ContextSection title="Message Analysis" color="#1a3a2e">
          <div class="context-section__kv">
            <div><span class="context-section__key">Total messages:</span> {String(messageAnalysis.total_messages ?? 0)}</div>
            {messageAnalysis.by_role && (
              <div>
                <span class="context-section__key">By role:</span>{' '}
                {Object.entries(messageAnalysis.by_role as Record<string, number>).map(([role, count]) => (
                  <span key={role} class="context-section__badge">{role}: {count}</span>
                ))}
              </div>
            )}
            {(messageAnalysis.cache_markers as number) > 0 && (
              <div><span class="context-section__key">Cache markers:</span> {String(messageAnalysis.cache_markers)}</div>
            )}
            {(messageAnalysis.tool_calls as number) > 0 && (
              <div><span class="context-section__key">Tool calls:</span> {String(messageAnalysis.tool_calls)}</div>
            )}
            {(messageAnalysis.tool_responses as number) > 0 && (
              <div><span class="context-section__key">Tool responses:</span> {String(messageAnalysis.tool_responses)}</div>
            )}
          </div>
        </ContextSection>
      )}

      {/* Full Message History */}
      {fullHistory && fullHistory.length > 0 && (
        <ContextSection title={`Full Message History (${fullHistory.length} messages)`} color="#2a1a3e">
          {fullHistory.map((msg, i) => {
            const role = String(msg.role || 'unknown')
            const content = msg.content
            let displayContent: string

            if (typeof content === 'string') {
              displayContent = content
            } else if (Array.isArray(content)) {
              displayContent = (content as Array<Record<string, string>>)
                .map((block) => {
                  if (block.type === 'text') return block.text
                  if (block.type === 'tool_use') return `[Tool call: ${block.name || 'unknown'}]`
                  if (block.type === 'tool_result') return `[Tool result: ${(block.content || '').toString().slice(0, 200)}]`
                  return `[${block.type || 'block'}]`
                })
                .join('\n')
            } else {
              displayContent = JSON.stringify(content, null, 2)
            }

            return (
              <div key={i} class={`context-message context-message--${role}`}>
                <div class="context-message__role">{role.toUpperCase()} (#{i})</div>
                <pre class="context-message__content">{displayContent}</pre>
              </div>
            )
          })}
        </ContextSection>
      )}
    </div>
  )
}

function ContextSection({ title, color, children }: { title: string; color: string; children: preact.ComponentChildren }) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div class="context-section" style={{ borderColor: color }}>
      <div class="context-section__title" style={{ background: color }} onClick={() => setCollapsed(!collapsed)}>
        <span>{collapsed ? '▶' : '▼'} {title}</span>
      </div>
      {!collapsed && <div class="context-section__body">{children}</div>}
    </div>
  )
}

// ── Memory Modal ──────────────────────────────────────────────
//
// Tabbed editor for the persona memory fields stored in
// `cloud_user_memory`: customization (note), user_details, future_events,
// and diary. Saving a field calls `updatePersonaMemoryField` and then
// asks the parent to refresh `cloudPersonas` so the modal reflects the
// new state. Mirrors the layout of the Flutter "Persona Browser" in
// `pipar/lib/screens/persona_customization_screen.dart`.

type ModalTab = 'persona' | 'user_details' | 'future_events' | 'diary'

const MEMORY_FIELD: Record<ModalTab, 'note' | 'user_details' | 'future_events' | 'diary'> = {
  persona: 'note',
  user_details: 'user_details',
  future_events: 'future_events',
  diary: 'diary',
}

const TAB_INFO: { key: ModalTab; label: string; icon: string }[] = [
  { key: 'persona',        label: 'Persona',        icon: '\u{2728}' },
  { key: 'user_details',   label: 'User Details',   icon: '\u{1F464}' },
  { key: 'future_events',  label: 'Future Events',  icon: '\u{1F4C5}' },
  { key: 'diary',          label: 'Diary',          icon: '\u{1F4D4}' },
]

function MemoryModal({
  personaName,
  personaImageUrl,
  personaId,
  userEmail,
  cloudPersona,
  initialSection,
  onClose,
  onPersonaUpdated,
}: {
  personaName: string
  personaImageUrl?: string
  personaId: string
  userEmail?: string
  cloudPersona: CloudPersona
  initialSection?: MemorySectionKey | null
  onClose: () => void
  onPersonaUpdated?: () => void
}) {
  const [isOwner, setIsOwner] = useState(false)
  const [builtInPersonality, setBuiltInPersonality] = useState<string>('')
  const [activeTab, setActiveTab] = useState<ModalTab>(
    (initialSection as ModalTab) || 'persona',
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Resolve subscription tier and (for owners) fetch the built-in
  // personality definition from the backend. Both fetches are best-effort:
  // non-owners get 403 from /debug/persona/* and we silently render only
  // the user customization. Network failures are also treated as "not
  // owner" / "no built-in description".
  useEffect(() => {
    if (!userEmail) return
    let cancelled = false
    Promise.all([
      getQuotaStatus(userEmail).catch(() => null),
      personaId ? getPersonaDescription(personaId) : Promise.resolve(null),
    ]).then(([status, description]) => {
      if (cancelled) return
      setIsOwner((status?.subscription_tier?.toLowerCase() ?? '') === 'owner')
      setBuiltInPersonality(description ?? '')
    })
    return () => { cancelled = true }
  }, [userEmail, personaId])

  // Re-sync the active tab if the parent passes a new initialSection
  // (i.e. the user clicked a different memory item in the persona panel
  // while the modal was already open).
  useEffect(() => {
    if (initialSection) setActiveTab(initialSection as ModalTab)
  }, [initialSection])

  const saveField = useCallback(async (tab: ModalTab, content: string) => {
    if (!personaId) return false
    setSaving(true)
    setSaveError(null)
    try {
      const ok = await updatePersonaMemoryField(personaId, MEMORY_FIELD[tab], content)
      if (!ok) {
        setSaveError('Failed to save changes. Please try again.')
        return false
      }
      onPersonaUpdated?.()
      return true
    } finally {
      setSaving(false)
    }
  }, [personaId, onPersonaUpdated])

  return (
    <div class="context-modal__overlay" onClick={onClose}>
      <div class="context-modal" onClick={(e) => e.stopPropagation()}>
        <div class="context-modal__header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {personaImageUrl ? (
              <img src={personaImageUrl} alt={personaName} style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />
            ) : null}
            {personaName} — Memory
          </h3>
          <button class="btn btn-sm" onClick={onClose}>Close</button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
        }}>
          {TAB_INFO.map((t) => {
            const active = activeTab === t.key
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  background: active ? 'var(--bg)' : 'transparent',
                  border: 'none',
                  borderBottom: active ? '2px solid #2DA5A0' : '2px solid transparent',
                  color: active ? '#2DA5A0' : 'var(--text-muted)',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 14 }}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            )
          })}
        </div>

        {saveError && (
          <div style={{
            padding: '8px 16px',
            background: 'rgba(255,68,68,0.12)',
            color: 'var(--danger, #ff4444)',
            fontSize: 12,
            borderBottom: '1px solid var(--border)',
          }}>{saveError}</div>
        )}

        <div class="context-modal__body">
          {activeTab === 'persona' && (
            <PersonaTab
              cloudPersona={cloudPersona}
              isOwner={isOwner}
              builtInPersonality={builtInPersonality}
              saving={saving}
              onSave={(content) => saveField('persona', content)}
            />
          )}
          {activeTab === 'user_details' && (
            <UserDetailsTab
              cloudPersona={cloudPersona}
              saving={saving}
              onSave={(content) => saveField('user_details', content)}
            />
          )}
          {activeTab === 'future_events' && (
            <FutureEventsTab
              cloudPersona={cloudPersona}
              saving={saving}
              onSave={(content) => saveField('future_events', content)}
            />
          )}
          {activeTab === 'diary' && (
            <DiaryTab
              cloudPersona={cloudPersona}
              personaId={personaId}
              personaName={personaName}
              saving={saving}
              onSave={(content) => saveField('diary', content)}
              onPersonaUpdated={onPersonaUpdated}
            />
          )}

          {/* AI Settings — read-only summary, always visible at the bottom */}
          {(cloudPersona.chat_model || cloudPersona.tts_voice) && (
            <div style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div style={memoryTitleStyle}>AI Settings</div>
              <div style={memoryContentStyle}>
                {cloudPersona.chat_model && <div>Chat model: {cloudPersona.chat_model}</div>}
                {cloudPersona.tts_voice && <div>TTS voice: {cloudPersona.tts_voice} ({cloudPersona.tts_provider})</div>}
                {cloudPersona.tts_speaking_style && <div>Speaking style: {cloudPersona.tts_speaking_style}</div>}
                {cloudPersona.speaking_speed !== 1.0 && <div>Speed: {cloudPersona.speaking_speed}x</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tab: Persona / Customization ───────────────────────────────

function PersonaTab({
  cloudPersona,
  isOwner,
  builtInPersonality,
  saving,
  onSave,
}: {
  cloudPersona: CloudPersona
  isOwner: boolean
  builtInPersonality: string
  saving: boolean
  onSave: (content: string) => Promise<boolean>
}) {
  return (
    <div>
      {/* Owners see the read-only built-in personality from
          config/prompts/persona_descriptions.json. Non-owners get a 403
          from /debug/persona/*, which we treat as "hide the section". */}
      {isOwner && builtInPersonality && (
        <MemoryStaticSection
          title="Built-in Personality (Fixed)"
          content={builtInPersonality}
          locked
        />
      )}
      <EditableSection
        title="Your Customization"
        content={cloudPersona.note || ''}
        emptyHint="No customizations yet. Click Edit to add your own personality tweaks or additional traits."
        saving={saving}
        onSave={onSave}
        allowClear
      />
    </div>
  )
}

// ── Tab: User Details ──────────────────────────────────────────

function UserDetailsTab({
  cloudPersona,
  saving,
  onSave,
}: {
  cloudPersona: CloudPersona
  saving: boolean
  onSave: (content: string) => Promise<boolean>
}) {
  return (
    <EditableSection
      title="User Details"
      content={cloudPersona.user_details || ''}
      emptyHint="No user details yet. Click Edit to record what you'd like the persona to remember about you."
      saving={saving}
      onSave={onSave}
      allowClear
    />
  )
}

// ── Tab: Future Events ─────────────────────────────────────────

function FutureEventsTab({
  cloudPersona,
  saving,
  onSave,
}: {
  cloudPersona: CloudPersona
  saving: boolean
  onSave: (content: string) => Promise<boolean>
}) {
  const raw = cloudPersona.future_events || ''
  const [events, parseError] = useMemo(() => {
    if (!raw.trim()) return [[] as Array<Record<string, unknown>>, null] as const
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return [parsed as Array<Record<string, unknown>>, null] as const
      return [[] as Array<Record<string, unknown>>, 'Stored value is not a JSON array'] as const
    } catch (e) {
      return [[] as Array<Record<string, unknown>>, (e as Error).message] as const
    }
  }, [raw])

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [rawEditing, setRawEditing] = useState(false)

  if (rawEditing) {
    return (
      <EditableSection
        title="Future Events (raw JSON)"
        content={raw || '[]'}
        emptyHint="No events. Edit to add a JSON array of events."
        saving={saving}
        onSave={async (c) => {
          const ok = await onSave(c)
          if (ok) setRawEditing(false)
          return ok
        }}
        onCancel={() => setRawEditing(false)}
        startInEditMode
        allowClear
        clearValue="[]"
      />
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={memoryTitleStyle}>Future Events</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            class="btn btn-sm"
            onClick={() => {
              const next = [...events, { description: '', approximate_date: '', category: '' }]
              onSave(JSON.stringify(next))
            }}
            disabled={saving}
            title="Add a blank event"
          >+ Add</button>
          <button
            class="btn btn-sm"
            onClick={() => setRawEditing(true)}
            title="Edit the raw JSON"
          >Edit JSON</button>
        </div>
      </div>

      {parseError && (
        <div style={{ ...memoryContentStyle, color: 'var(--danger, #ff4444)', marginBottom: 12 }}>
          Could not parse stored future_events: {parseError}. Use "Edit JSON" to fix it.
        </div>
      )}

      {events.length === 0 ? (
        <div style={{ ...memoryContentStyle, fontStyle: 'italic' }}>
          No future events recorded.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((ev, i) => (
            <FutureEventCard
              key={i}
              event={ev}
              editing={editingIndex === i}
              saving={saving}
              onEdit={() => setEditingIndex(i)}
              onCancel={() => setEditingIndex(null)}
              onSave={async (next) => {
                const updated = [...events]
                updated[i] = { ...updated[i], ...next }
                const ok = await onSave(JSON.stringify(updated))
                if (ok) setEditingIndex(null)
                return ok
              }}
              onDelete={async () => {
                const updated = events.filter((_, idx) => idx !== i)
                const ok = await onSave(JSON.stringify(updated))
                if (ok && editingIndex === i) setEditingIndex(null)
                return ok
              }}
            />
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
        {events.length} event{events.length === 1 ? '' : 's'} · {raw.length} characters
      </div>
    </div>
  )
}

function FutureEventCard({
  event,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  event: Record<string, unknown>
  editing: boolean
  saving: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (next: { description: string; approximate_date: string }) => Promise<boolean>
  onDelete: () => Promise<boolean>
}) {
  const description = String(event.description ?? event.event ?? '')
  const date = String(event.approximate_date ?? '')
  const category = String(event.category ?? '')
  const source = String(event.source ?? '')

  const [draftDesc, setDraftDesc] = useState(description)
  const [draftDate, setDraftDate] = useState(date)

  useEffect(() => {
    if (editing) {
      setDraftDesc(description)
      setDraftDate(date)
    }
  }, [editing, description, date])

  if (editing) {
    return (
      <div style={cardStyle}>
        <label style={fieldLabelStyle}>Description</label>
        <textarea
          value={draftDesc}
          onInput={(e) => setDraftDesc((e.target as HTMLTextAreaElement).value)}
          rows={3}
          style={textareaStyle}
        />
        <label style={fieldLabelStyle}>Approximate date</label>
        <input
          value={draftDate}
          onInput={(e) => setDraftDate((e.target as HTMLInputElement).value)}
          style={{ ...textareaStyle, minHeight: 0, fontFamily: 'inherit' }}
        />
        {category && (
          <div style={{ marginTop: 6 }}>
            <span style={categoryChipStyle}>{category}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
          <button
            class="btn btn-sm"
            style={{ color: 'var(--danger, #ff4444)' }}
            onClick={() => { void onDelete() }}
            disabled={saving}
          >Delete</button>
          <div style={{ flex: 1 }} />
          <button class="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            class="btn btn-sm btn-primary"
            disabled={saving}
            onClick={() => { void onSave({ description: draftDesc.trim(), approximate_date: draftDate.trim() }) }}
          >{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{ ...cardStyle, cursor: 'pointer' }}
      onClick={onEdit}
      title="Click to edit"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{'\u{1F4C5}'}</span>
        <strong style={{ flex: 1 }}>{description || '(no description)'}</strong>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>edit</span>
      </div>
      {date && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{'\u{23F0}'} {date}</div>}
      {category && <div style={{ marginTop: 6 }}><span style={categoryChipStyle}>{category}</span></div>}
      {source && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 4 }}>
          Source: {source}
        </div>
      )}
    </div>
  )
}

// ── Tab: Diary (daily browser) ─────────────────────────────────
//
// Mirrors the Flutter "Persona Browser" diary tab. Two data sources:
//
//   1. The per-day archive in `cloud_diary_entries`, fetched via
//      listCloudDiaryEntries(). Mobile is the source of truth — it
//      pushes each rolled-over day after writing locally. Personas
//      marked Always Local on mobile never reach this table.
//   2. The current in-progress diary in cloud_user_memory.diary
//      (cloudPersona.diary), shown as a "current" card stamped with
//      today's date so the user can see what's still being written
//      before the next midnight rollover.
//
// Tags extracted from `#hashtags` and a few semantic keywords mirror
// what the mobile UI does for the in-progress diary; archived entries
// already carry their `tags` array from the server.

interface DiaryEntryView {
  date: string          // YYYY-MM-DD
  content: string
  tags: string[]
  isContext: boolean    // true for the in-progress current diary
  updatedAt?: string | null
}

function extractDiaryTags(content: string): string[] {
  const tags = new Set<string>()
  const hashtagRegex = /#(\w+)/g
  let match: RegExpExecArray | null
  while ((match = hashtagRegex.exec(content)) !== null) {
    if (match[1]) tags.add(match[1].toLowerCase())
  }
  const lower = content.toLowerCase()
  if (/\b(work|meeting|project|task|office)\b/.test(lower)) tags.add('work')
  if (/\b(test|testing|app|application|feature)\b/.test(lower)) tags.add('testing')
  if (/\b(image|picture|photo|generated|create)\b/.test(lower)) tags.add('creative')
  if (/\b(first|new|initial|introduction)\b/.test(lower)) tags.add('first-time')
  return Array.from(tags)
}

function todayKey(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function monthKeyFromDate(dateStr: string): string {
  // dateStr is YYYY-MM-DD
  return dateStr.slice(0, 7)
}

function formatMonthLabel(monthKey: string): string {
  try {
    const [y, m] = monthKey.split('-')
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${monthNames[parseInt(m, 10) - 1]} ${y}`
  } catch {
    return monthKey
  }
}

interface DiaryTabProps {
  cloudPersona: CloudPersona
  personaId: string
  personaName: string
  saving: boolean
  /** Save handler for the in-progress diary (cloud_user_memory.diary). */
  onSave: (content: string) => Promise<boolean>
  /** Refresh persona-level memory after archived edits/deletes — keeps
   *  the right-rail panel in sync. */
  onPersonaUpdated?: () => void
}

function DiaryTab({
  cloudPersona,
  personaId,
  personaName,
  saving,
  onSave,
  onPersonaUpdated,
}: DiaryTabProps) {
  const [archived, setArchived] = useState<CloudDiaryEntry[]>([])
  const [serverMonths, setServerMonths] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string>('All')
  const [editingDate, setEditingDate] = useState<string | null>(null)
  const [archivedSaving, setArchivedSaving] = useState(false)

  // Reload archive whenever persona or selected month changes. The
  // server filters by month server-side so we don't pull large archives
  // unnecessarily; "Recent" passes no month and lets the server return
  // everything (newest-first).
  const reload = useCallback(async () => {
    if (!personaId) return
    setLoading(true)
    setLoadError(null)
    try {
      const res = await listCloudDiaryEntries(personaId, selectedMonth || undefined)
      setArchived(res.entries)
      setServerMonths(res.months)
    } catch (e) {
      setLoadError((e as Error).message || 'Failed to load diary entries')
    } finally {
      setLoading(false)
    }
  }, [personaId, selectedMonth])

  useEffect(() => { void reload() }, [reload])

  // Build the merged view: archived entries from the server plus, when
  // viewing "Recent" or the current month, the in-progress current
  // diary as a synthetic "today" card.
  const todayMonth = todayKey().slice(0, 7)
  const showContext =
    !!cloudPersona.diary?.trim()
    && (selectedMonth === null || selectedMonth === todayMonth)
    // If today already has an archived entry, mobile has rolled it over
    // — don't show a duplicate card.
    && !archived.some((a) => a.date === todayKey())

  const merged: DiaryEntryView[] = useMemo(() => {
    const list: DiaryEntryView[] = archived.map((a) => ({
      date: a.date,
      content: a.content,
      tags: Array.isArray(a.tags) ? a.tags : [],
      isContext: false,
      updatedAt: a.updated_at,
    }))
    if (showContext) {
      list.push({
        date: todayKey(),
        content: cloudPersona.diary || '',
        tags: extractDiaryTags(cloudPersona.diary || ''),
        isContext: true,
      })
    }
    list.sort((a, b) => b.date.localeCompare(a.date))
    return list
  }, [archived, showContext, cloudPersona.diary])

  // Months come from the server; if today's in-progress diary exists
  // and the current month isn't already in the list, surface it so the
  // user can filter to "this month" and see the synthetic card.
  const availableMonths = useMemo(() => {
    const set = new Set(serverMonths)
    if (showContext) set.add(todayMonth)
    return Array.from(set).sort().reverse()
  }, [serverMonths, showContext, todayMonth])

  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const e of merged) e.tags.forEach((t) => set.add(t))
    return Array.from(set).sort()
  }, [merged])

  const filtered = merged.filter((e) => {
    if (tagFilter !== 'All' && !e.tags.some((t) => t.toLowerCase().includes(tagFilter.toLowerCase()))) return false
    return true
  })

  const handleSaveEntry = async (entry: DiaryEntryView, content: string): Promise<boolean> => {
    if (entry.isContext) {
      // Edit the current in-progress diary via cloud_user_memory.diary.
      return onSave(content)
    }
    setArchivedSaving(true)
    try {
      const ok = await upsertCloudDiaryEntry(personaId, entry.date, content, entry.tags)
      if (ok) {
        await reload()
        onPersonaUpdated?.()
      }
      return ok
    } finally {
      setArchivedSaving(false)
    }
  }

  const handleClearEntry = async (entry: DiaryEntryView): Promise<boolean> => {
    if (entry.isContext) {
      // Clearing the in-progress diary means writing an empty string.
      return onSave('')
    }
    setArchivedSaving(true)
    try {
      const ok = await deleteCloudDiaryEntry(personaId, entry.date)
      if (ok) {
        await reload()
        onPersonaUpdated?.()
      }
      return ok
    } finally {
      setArchivedSaving(false)
    }
  }

  const busy = saving || archivedSaving

  return (
    <div>
      {/* Month filter chip row */}
      <div style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        paddingBottom: 8,
        marginBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}>
        <FilterChip
          label="Recent"
          active={selectedMonth === null}
          onClick={() => setSelectedMonth(null)}
        />
        {availableMonths.map((m) => (
          <FilterChip
            key={m}
            label={formatMonthLabel(m)}
            active={selectedMonth === m}
            onClick={() => setSelectedMonth(m)}
          />
        ))}
        <div style={{ flex: 1 }} />
        {allTags.length > 0 && (
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter((e.target as HTMLSelectElement).value)}
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              color: 'var(--text)',
              fontSize: 12,
              padding: '4px 10px',
            }}
          >
            <option value="All">All tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>#{t}</option>
            ))}
          </select>
        )}
      </div>

      {/* Summary header */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        {loading
          ? 'Loading diary entries…'
          : `${filtered.length} ${filtered.length === 1 ? 'entry' : 'entries'}`}
        {!loading && selectedMonth && ` in ${formatMonthLabel(selectedMonth)}`}
        {!loading && tagFilter !== 'All' && ` (filtered: #${tagFilter})`}
      </div>

      {loadError && (
        <div style={{ ...memoryContentStyle, color: 'var(--danger, #ff4444)', marginBottom: 12 }}>
          {loadError}
        </div>
      )}

      {!loading && filtered.length === 0 ? (
        <div style={{ ...memoryContentStyle, textAlign: 'center', fontStyle: 'italic' }}>
          {merged.length === 0
            ? `No diary entries for ${personaName} yet. Entries are pushed from the mobile app each day; if this persona is set to "Always Local" on mobile, its diary stays on the device.`
            : 'No entries match the selected filter.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((entry) => (
            <DiaryEntryCard
              key={entry.date}
              entry={entry}
              editing={editingDate === entry.date}
              saving={busy}
              onEdit={() => setEditingDate(entry.date)}
              onCancel={() => setEditingDate(null)}
              onSave={async (content) => {
                const ok = await handleSaveEntry(entry, content)
                if (ok) setEditingDate(null)
                return ok
              }}
              onClear={async () => {
                const ok = await handleClearEntry(entry)
                if (ok) setEditingDate(null)
                return ok
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px',
        borderRadius: 16,
        border: '1px solid ' + (active ? '#2DA5A0' : 'var(--border)'),
        background: active ? 'rgba(45,165,160,0.18)' : 'transparent',
        color: active ? '#2DA5A0' : 'var(--text)',
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >{label}</button>
  )
}

function DiaryEntryCard({
  entry,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
  onClear,
}: {
  entry: DiaryEntryView
  editing: boolean
  saving: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (content: string) => Promise<boolean>
  onClear: () => Promise<boolean>
}) {
  const [draft, setDraft] = useState(entry.content)
  useEffect(() => { if (editing) setDraft(entry.content) }, [editing, entry.content])

  if (editing) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <strong style={{ flex: 1 }}>{entry.date}</strong>
          {entry.isContext && (
            <span style={{ ...categoryChipStyle, background: 'rgba(45,165,160,0.2)', color: '#2DA5A0' }}>
              current
            </span>
          )}
        </div>
        <textarea
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          rows={10}
          style={textareaStyle}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
          <button
            class="btn btn-sm"
            style={{ color: 'var(--warning, #ff9800)' }}
            onClick={() => { void onClear() }}
            disabled={saving}
            title="Clear the diary"
          >Clear</button>
          <div style={{ flex: 1 }} />
          <button class="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            class="btn btn-sm btn-primary"
            disabled={saving}
            onClick={() => { void onSave(draft) }}
          >{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{ ...cardStyle, cursor: 'pointer' }}
      onClick={onEdit}
      title="Click to edit"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 14 }}>{'\u{1F4D6}'}</span>
        <strong style={{ flex: 1 }}>{entry.date}</strong>
        {entry.isContext && (
          <span style={{ ...categoryChipStyle, background: 'rgba(45,165,160,0.2)', color: '#2DA5A0' }}>
            current
          </span>
        )}
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>edit</span>
      </div>
      <div style={{
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 220,
        overflow: 'auto',
        marginBottom: entry.tags.length > 0 ? 8 : 0,
      }}>{entry.content}</div>
      {entry.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {entry.tags.map((t) => (
            <span key={t} style={categoryChipStyle}>#{t}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Editable section (used by Persona / User Details / raw JSON) ──

function EditableSection({
  title,
  content,
  emptyHint,
  saving,
  onSave,
  onCancel,
  startInEditMode = false,
  allowClear = false,
  clearValue = '',
}: {
  title: string
  content: string
  emptyHint?: string
  saving: boolean
  onSave: (content: string) => Promise<boolean>
  onCancel?: () => void
  startInEditMode?: boolean
  allowClear?: boolean
  clearValue?: string
}) {
  const [editing, setEditing] = useState(startInEditMode)
  const [draft, setDraft] = useState(content)

  useEffect(() => {
    if (!editing) setDraft(content)
  }, [editing, content])

  if (editing) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={memoryTitleStyle}>{title}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{draft.length} chars</div>
        </div>
        <textarea
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          rows={12}
          style={{ ...textareaStyle, minHeight: 240 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
          {allowClear && (
            <button
              class="btn btn-sm"
              style={{ color: 'var(--warning, #ff9800)' }}
              onClick={() => setDraft(clearValue)}
              disabled={saving}
              title="Clear the field"
            >Clear</button>
          )}
          <div style={{ flex: 1 }} />
          <button
            class="btn btn-sm"
            onClick={() => {
              if (onCancel) onCancel()
              else setEditing(false)
            }}
            disabled={saving}
          >Cancel</button>
          <button
            class="btn btn-sm btn-primary"
            disabled={saving}
            onClick={async () => {
              const ok = await onSave(draft)
              if (ok) setEditing(false)
            }}
          >{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={memoryTitleStyle}>{title}</div>
        <button class="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>
      </div>
      <div style={memoryContentStyle}>
        {content?.trim()
          ? content
          : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{emptyHint || 'Empty'}</span>}
      </div>
      {content?.trim() && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {content.length} characters
        </div>
      )}
    </div>
  )
}

function MemoryStaticSection({ title, content, locked }: { title: string; content: string; locked?: boolean }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={memoryTitleStyle}>{title}</div>
        {locked && <span style={{ fontSize: 12, color: 'var(--text-muted)' }} title="Read-only">{'\u{1F512}'}</span>}
      </div>
      <div style={memoryContentStyle}>
        {content?.trim() ? content : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Empty</span>}
      </div>
    </div>
  )
}

// ── Shared styles ──────────────────────────────────────────────

const memoryTitleStyle: Record<string, string | number> = {
  fontSize: 13,
  fontWeight: 600,
  color: '#2DA5A0',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}

const memoryContentStyle: Record<string, string | number> = {
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'rgba(250,250,250,0.7)',
  background: 'var(--bg-surface)',
  borderRadius: 8,
  padding: 12,
  border: '1px solid var(--border)',
  maxHeight: 300,
  overflow: 'auto',
}

const cardStyle: Record<string, string | number> = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 12,
  fontSize: 13,
  color: 'var(--text)',
}

const fieldLabelStyle: Record<string, string | number> = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 4,
  marginTop: 8,
}

const textareaStyle: Record<string, string | number> = {
  width: '100%',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  padding: 10,
  fontSize: 13,
  lineHeight: 1.5,
  fontFamily: 'inherit',
  resize: 'vertical',
  boxSizing: 'border-box',
}

const categoryChipStyle: Record<string, string | number> = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  background: 'rgba(45,165,160,0.18)',
  color: '#2DA5A0',
  fontSize: 11,
  fontWeight: 500,
}
